import { fabric } from 'fabric';
import { Injector } from '@angular/core';
import { DomListener } from 'src/app/utilities/DomListener';
import { DomListenerFactoryService } from 'src/app/services/dom-listener-factory.service';
import { ItemModel } from 'src/app/models/itemModel';
import { filter, Subscription } from 'rxjs';
import { LINE_TYPE } from '../../context-menus/fabric-object-settings';
import { BoardItemsInteractionsManager } from '../board-items-interactions-manager';
import { CreationContext } from './board-item-configurations';
import { BoardItemComponentClass, FabricControls } from './board.item';
import { LineConfigurations } from './board-line-configurations';

export class FabricLinePath extends fabric.Path {
  constructor(value: any) {
    super('', { ...value, opacity: 1 });
  }

  static _fromObject(
    className: string,
    object: fabric.Object,
    callback?: Function,
    extraParam?: any,
  ): fabric.Object {
    return fabric.Object._fromObject(className, object, callback, extraParam);
  }
}

export enum LineControlKey {
  START = 'start',
  END = 'end',
}

// constants
const ARROW_HEAD_LENGTH_FACTOR = 3;
const ARROW_HEAD_ANGLE_OFFSET_RAD = Math.PI / 6;
const LINE_MIN_LENGTH_PX = 6;

export class BoardLine extends BoardItemComponentClass<LineConfigurations>(FabricLinePath) {
  // used to track moving a single board line shape
  private previousLeft: number | undefined;
  private previousTop: number | undefined;
  private previousStartPoint: fabric.Point | undefined;
  private previousEndPoint: fabric.Point | undefined;

  // used to track moving a multi selection containing board line shape
  private groupLeft: number | undefined;
  private groupTop: number | undefined;

  private selectionSubscription?: Subscription;

  private boardItemsInteractionsManagerService: BoardItemsInteractionsManager;
  domListener: DomListener;

  constructor(
    injector: Injector,
    configurations: LineConfigurations,
    creationContext: CreationContext,
  ) {
    super(injector, ItemModel.Line, configurations, Object.values(FabricControls), creationContext);
    this.set({
      stroke: this.configurations.strokeColor,
      strokeWidth: this.configurations.strokeWidth,
      fill: 'transparent',
      selectable: true,
      hasControls: true,
      hasBorders: false,
      strokeLineCap: 'round',
      strokeLineJoin: 'round',
      excludeFromExport: false,
      absolutePositioned: true,
      perPixelTargetFind: true,
      strokeDashArray: this.getStrokeDashArray(),
    } as Partial<this>);
    // to avoid board line from being cached when caching is enabled for fabric.Path objects
    this.objectCaching = false;
    this.boardItemsInteractionsManagerService = injector.get<BoardItemsInteractionsManager>(
      BoardItemsInteractionsManager,
    );
    const domListenerFactory = injector.get(DomListenerFactoryService);
    this.domListener = domListenerFactory.createInstance();
  }

  /**
   * Redraws the path using configuration data
   */
  public rebuildPath() {
    const pathData = this.buildPathString(
      this.configurations.startPoint.x,
      this.configurations.startPoint.y,
      this.configurations.endPoint.x,
      this.configurations.endPoint.y,
    );
    const parsedPath = (fabric.util as any).parsePath(pathData);

    // Let Fabric auto-calculate bounding box from new path
    // This triggers internal width/height update
    (this as any)._setPath(parsedPath);
    this.setCoords();
    this.updateOldCoordinates();
    this.canvas?.requestRenderAll();
  }

  public setType(type: LINE_TYPE) {
    if (type === this.configurations.type) {
      return;
    }
    this.configurations.type = type;
    this.set({ strokeDashArray: this.getStrokeDashArray() } as Partial<this>);
    this.rebuildPath();
    this.syncChangesWithServer();
  }

  public setStrokeColor(color: string) {
    if (color === this.configurations.strokeColor) {
      return;
    }
    this.configurations.strokeColor = color;
    this.set({ stroke: color } as Partial<this>);
    this.canvas?.requestRenderAll();
    this.syncChangesWithServer();
  }

  public setStrokeWidth(strokeWidth: number) {
    if (strokeWidth === this.configurations.strokeWidth) {
      return;
    }
    this.configurations.strokeWidth = strokeWidth;
    this.set({ strokeWidth: strokeWidth } as Partial<this>);
    this.set({ strokeDashArray: this.getStrokeDashArray() } as Partial<this>);
    this.rebuildPath();
    this.syncChangesWithServer();
  }

  public getLineLength(): number {
    return Math.sqrt((this.width ?? 0) ** 2 + (this.height ?? 0) ** 2);
  }

  public getMinLength(): number {
    return LINE_MIN_LENGTH_PX;
  }

  public updateOldCoordinates() {
    this.previousLeft = this.left;
    this.previousTop = this.top;
    this.previousStartPoint = this.configurations.startPoint;
    this.previousEndPoint = this.configurations.endPoint;
  }

  private buildPathString(x1: number, y1: number, x2: number, y2: number) {
    if (
      this.configurations.type === LINE_TYPE.LINE ||
      this.configurations.type === LINE_TYPE.DASHED
    ) {
      return `M ${x1} ${y1} L ${x2} ${y2}`;
    } else {
      // Avoid drawing arrow head when the line has zero length (initial mouse down)
      if (x1 === x2 && y1 === y2) {
        return `M ${x1} ${y1} L ${x2} ${y2}`;
      }
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const headLength = this.configurations.strokeWidth * ARROW_HEAD_LENGTH_FACTOR;
      const hx1 = x2 - headLength * Math.cos(angle - ARROW_HEAD_ANGLE_OFFSET_RAD);
      const hy1 = y2 - headLength * Math.sin(angle - ARROW_HEAD_ANGLE_OFFSET_RAD);
      const hx2 = x2 - headLength * Math.cos(angle + ARROW_HEAD_ANGLE_OFFSET_RAD);
      const hy2 = y2 - headLength * Math.sin(angle + ARROW_HEAD_ANGLE_OFFSET_RAD);

      return `
      M ${x1} ${y1}
      L ${x2} ${y2}
      M ${hx1} ${hy1}
      L ${x2} ${y2}
      L ${hx2} ${hy2}
    `;
    }
  }

  private getStrokeDashArray(): number[] | undefined {
    if (this.configurations.type !== LINE_TYPE.DASHED) {
      return undefined;
    }
    switch (this.configurations.strokeWidth) {
      case 2:
      case 4:
        return [10, 10];
      case 8:
      case 12:
        return [10, 20];
      default:
        return [10, 25];
    }
  }

  private initializeControls() {
    // Create a new controls object for this instance to avoid sharing between instances
    this.controls = { ...this.controls };

    // Ensure all default controls are hidden
    this.setControlsVisibility({
      tr: false,
      bl: false,
      ml: false,
      mt: false,
      mr: false,
      mb: false,
      mtr: false,
      tl: false,
      br: false,
    });

    // create controls for start and end points
    this.controls.start = this.createControl(LineControlKey.START);
    this.controls.end = this.createControl(LineControlKey.END);
  }

  private createControl(controlName: LineControlKey) {
    return new fabric.Control({
      positionHandler: (dim, finalMatrix, fabricObject) => {
        const vpt = fabricObject.canvas?.viewportTransform || fabric.iMatrix;
        const controlPoint =
          controlName === LineControlKey.START
            ? this.configurations.startPoint
            : this.configurations.endPoint;
        return fabric.util.transformPoint(controlPoint, vpt);
      },
      mouseDownHandler: (eventData, transform, x, y) => {
        // attach shift key listeners to have instant snapping when shift is held
        this.domListener.clear();
        this.domListener.add(
          document,
          'keydown.shift',
          this.handleShiftKeyDown.bind(this, controlName),
          true,
        );
        this.domListener.add(
          document,
          'keyup.shift',
          this.handleShiftKeyUp.bind(this, controlName),
          true,
        );
        return true;
      },
      actionHandler: (eventData, transform, x, y) => {
        const isShiftHeld = ((eventData as MouseEvent)?.shiftKey ?? false) === true;
        const controlPoint = this.computeControlPointPosition(controlName, isShiftHeld);
        if (controlName === LineControlKey.START) {
          this.configurations.startPoint = new fabric.Point(controlPoint.x, controlPoint.y);
        } else {
          this.configurations.endPoint = new fabric.Point(controlPoint.x, controlPoint.y);
        }
        this.rebuildPath();
        // mimic scaling event so context menu hides while dragging
        this.fire('scaling');
        return true;
      },
      mouseUpHandler: () => {
        // signal end of interaction to show context menu again and persist state
        this.domListener.clear();
        this.fire('modified');
        return true;
      },
      cursorStyle: 'crosshair',
      render: fabric.Control.prototype.render,
    });
  }

  // return the control point position based on the control name and whether to snap
  private computeControlPointPosition(controlName: LineControlKey, shouldSnap: boolean) {
    const currentPoint =
      this.sharedDataService.lastMouseLocation?.pointer ?? new fabric.Point(0, 0);
    if (!shouldSnap) {
      return currentPoint;
    }
    const fixedPoint = this.getFixedPoint(controlName);
    const snapped = this.getSnappedPoint(currentPoint?.x ?? 0, currentPoint?.y ?? 0, fixedPoint);
    return snapped;
  }

  private handleShiftKeyDown(controlName: LineControlKey) {
    const snapped = this.computeControlPointPosition(controlName, true);
    if (controlName === LineControlKey.START) {
      this.configurations.startPoint = snapped;
    } else {
      this.configurations.endPoint = snapped;
    }
    this.rebuildPath();
  }

  private handleShiftKeyUp(controlName: LineControlKey) {
    const currentPoint = this.sharedDataService.lastMouseLocation?.pointer;
    if (!currentPoint) {
      return;
    }
    const unsnappedPoint = new fabric.Point(currentPoint.x, currentPoint.y);
    if (controlName === LineControlKey.START) {
      this.configurations.startPoint = unsnappedPoint;
    } else {
      this.configurations.endPoint = unsnappedPoint;
    }
    this.rebuildPath();
  }

  private getFixedPoint(controlName: LineControlKey) {
    return controlName === LineControlKey.START
      ? this.configurations.endPoint
      : this.configurations.startPoint;
  }

  /**
   * If multi selection, we need to update the starting values of group left and top
   * we use this to calculate the delta when moving the group
   * If the line is locked, we need to show the borders
   */
  private setupSelectionSubscription() {
    this.selectionSubscription = this.boardItemsInteractionsManagerService.selection$
      .pipe(filter((selection) => selection?.object === this || selection?.object === this.group))
      .subscribe((selection) => {
        if (selection?.isMultiSelection) {
          this.groupLeft = (selection?.object as fabric.ActiveSelection)?.left;
          this.groupTop = (selection?.object as fabric.ActiveSelection)?.top;
        }
        if (this.configurations.locked) {
          this.hasBorders = true;
        } else {
          this.hasBorders = false;
        }
      });
  }

  protected override onFabricAdded(): void {
    super.onFabricAdded();
    this.rebuildPath();
    this.initializeControls();
    this.updateOldCoordinates();
    this.setupSelectionSubscription();
  }

  protected override onFabricRemoved() {
    super.onFabricRemoved();
    this.selectionSubscription?.unsubscribe();
  }

  /**
   * On moving we need to update start and end points so that the position of the controls is updated
   */
  protected override onMoving(): void {
    super.onMoving();
    let deltaX = 0;
    let deltaY = 0;
    const active = this.canvas?.getActiveObject();
    if (active === this.group) {
      deltaX = (active?.left ?? 0) - (this.groupLeft ?? 0);
      deltaY = (active?.top ?? 0) - (this.groupTop ?? 0);
    } else {
      // single selection
      deltaX = (this.left ?? 0) - (this.previousLeft ?? 0);
      deltaY = (this.top ?? 0) - (this.previousTop ?? 0);
    }

    this.configurations.startPoint = new fabric.Point(
      (this.previousStartPoint?.x ?? 0) + deltaX,
      (this.previousStartPoint?.y ?? 0) + deltaY,
    );

    this.configurations.endPoint = new fabric.Point(
      (this.previousEndPoint?.x ?? 0) + deltaX,
      (this.previousEndPoint?.y ?? 0) + deltaY,
    );
  }

  protected override onMouseDown(e: any): void {
    super.onMouseDown(e);
    this.updateOldCoordinates();
  }

  protected override onDeselection(): void {
    super.onDeselection();
    this.updateOldCoordinates();
  }

  protected override setStateFromConfigurations(configurations: LineConfigurations) {
    super.setStateFromConfigurations(configurations);
    this.configurations.startPoint = configurations.startPoint;
    this.configurations.endPoint = configurations.endPoint;
    this.configurations.type = configurations.type;
    this.configurations.strokeColor = configurations.strokeColor;
    this.configurations.strokeWidth = configurations.strokeWidth;
    this.set({
      stroke: configurations.strokeColor,
      strokeWidth: configurations.strokeWidth,
      strokeDashArray: this.getStrokeDashArray(),
    } as Partial<this>);
    this.rebuildPath();
  }

  /**
   * Ensure serialization retains full fabric path data + our additional fields
   */
  override toObject(propertiesToInclude: string[] = []): any {
    const base = super.toObject([...propertiesToInclude]);
    base.vuid = this.vuid;
    base.uid = this.uid;
    base.userId = this.configurations.ownerId;
    base.order = this.configurations.order;
    return base;
  }

  isStoredAsFrameItem(): boolean {
    return false;
  }

  override closeLock() {
    super.closeLock();
    this.setControlVisible(LineControlKey.START, false);
    this.setControlVisible(LineControlKey.END, false);
    this.hasBorders = true;
  }

  override openLock() {
    super.openLock();
    this.setControlVisible(LineControlKey.START, true);
    this.setControlVisible(LineControlKey.END, true);
    this.hasBorders = false;
  }

  override handleInitializationFromPaste(deltaX: number, deltaY: number): void {
    super.handleInitializationFromPaste(deltaX, deltaY);
    this.configurations.startPoint = new fabric.Point(
      (this.configurations.startPoint?.x ?? 0) + deltaX,
      (this.configurations.startPoint?.y ?? 0) + deltaY,
    );

    this.configurations.endPoint = new fabric.Point(
      (this.configurations.endPoint?.x ?? 0) + deltaX,
      (this.configurations.endPoint?.y ?? 0) + deltaY,
    );
  }

  /** Snap the free endpoint of the line to the nearest 45-degree angle */
  public getSnappedPoint(targetX: number, targetY: number, fixedPoint: fabric.Point): fabric.Point {
    const dx = targetX - (fixedPoint?.x ?? 0);
    const dy = targetY - (fixedPoint?.y ?? 0);
    const angle = Math.atan2(dy, dx);
    const step = Math.PI / 4; // 45 degrees
    const snappedAngle = Math.round(angle / step) * step;
    const distance = Math.hypot(dx, dy);
    targetX = (fixedPoint?.x ?? 0) + distance * Math.cos(snappedAngle);
    targetY = (fixedPoint?.y ?? 0) + distance * Math.sin(snappedAngle);
    return new fabric.Point(targetX, targetY);
  }
}
